'use client';

import SearchBold from 'icons/lib/magnifier_fat.svg';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { MouseEvent as ReactMouseEvent, useState } from 'react';
import { Button, TextField } from 'ui';

import { useTranslation } from '@/i18n/client';
import { routes } from '@/shared/data/routes';
import {
  useCurrentLanguageCode,
  useCurrentLocale,
} from '@/shared/utils/client';

import { Brand } from '../../api';
import styles from './RegularBrands.module.scss';
import { RegularBrandsProps } from './types';

export const RegularBrands = ({ regularBrands }: RegularBrandsProps) => {
  const { t } = useTranslation('brands');
  const locale = useCurrentLocale();
  const languageCode = useCurrentLanguageCode();
  const [filteredRegularBrands, setFilteredRegularBrands] =
    useState(regularBrands);
  const [search, setSearch] = useState('');
  const router = useRouter();
  const pathname = usePathname();

  const onSearch = () => {
    const filteredBrands = regularBrands.map((brand) => ({
      ...brand,
      brands: brand.brands.filter((b) =>
        b.name.name
          .toLocaleLowerCase()
          .trim()
          .includes(search.toLocaleLowerCase().trim())
      ),
    }));

    setFilteredRegularBrands(filteredBrands);
  };

  const handleLetterClick = (
    e: ReactMouseEvent<HTMLAnchorElement, MouseEvent>,
    id: string
  ) => {
    e.preventDefault();

    const container = document.getElementById(id);

    if (!container) {
      return;
    }

    container.scrollIntoView({ behavior: 'smooth' });

    router.push(`${routes[locale].BRANDS_LIST}#${id}`);
  };

  const handleBrandNameSort = (a: Brand, b: Brand) => {
    return a.name.name
      .toLocaleLowerCase(locale)
      .localeCompare(b.name.name.toLocaleLowerCase(locale), languageCode);
  };

  return (
    <div className={styles.regularBrands}>
      <div className="container">
        <div className={styles.regularBrands__search}>
          <TextField
            fullWidth
            placeholder={t('brands.searchPlaceholder') as string}
            suffix={
              <Button
                size="small"
                variant="iconButton"
                buttonName="textFieldSearchButton"
                onPress={onSearch}
              >
                <SearchBold />
              </Button>
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.code === 'Enter' && onSearch()}
          />
        </div>
        <div className={styles.regularBrands__alphabet}>
          <div className={styles.regularBrands__alphabetWrapper}>
            {filteredRegularBrands.map((brand) => {
              if (brand.brands.length < 1) {
                return null;
              }

              return (
                <Link
                  key={`letter-${brand.letter}`}
                  href={`${routes[locale].BRANDS_LIST}#brand-${brand.letter}`}
                  prefetch={false}
                  onClick={(e) => handleLetterClick(e, `brand-${brand.letter}`)}
                >
                  {brand.letter}
                </Link>
              );
            })}
          </div>
        </div>
        {filteredRegularBrands.map((brand) => {
          if (brand.brands.length < 1) {
            return null;
          }

          return (
            <div
              id={`brand-${brand.letter}`}
              key={brand.letter}
              className={styles.regularBrands__brands}
            >
              <div className={styles.regularBrands__brands__letter}>
                <h4>{brand.letter}</h4>
                <div className={styles.regularBrands__brands__letter__items}>
                  {brand.brands.sort(handleBrandNameSort).map((b) => (
                    <Link
                      key={b.brandId}
                      href={`${routes[locale].SINGLE_BRAND}/${b.name.slug}`}
                      prefetch={false}
                    >
                      {b.name.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
